import type { Compare, Optional, OptionalArray } from '../types/types'
import { isEmpty, notEmpty } from './typeUtils.ts'

export { default as partition } from 'lodash-es/partition.js'

export const isCompare = <I, O extends I>(
  value: Compare<I>,
  guard: (v: I) => v is O,
): value is Compare<O> =>
  (!value.compare || guard(value.compare)) &&
  (!value.current || guard(value.current))

export const getListOfPairs = <T>({
  currentList,
  prevList,
  keyFunc,
}: {
  currentList: OptionalArray<T>
  prevList: OptionalArray<T>
  keyFunc: (x: T) => Optional<string>
}): Compare<T>[] => {
  const keyToValues: Record<string, Compare<T>> = {}

  currentList?.filter(notEmpty).forEach((value) => {
    const key = keyFunc(value)

    if (isEmpty(key)) {
      return
    }

    if (keyToValues[key] === undefined) {
      keyToValues[key] = { current: value, compare: undefined }
    } else {
      keyToValues[key].current = value
    }
  })

  prevList?.filter(notEmpty).forEach((value) => {
    const key = keyFunc(value)

    if (isEmpty(key)) {
      return
    }

    if (keyToValues[key] === undefined) {
      keyToValues[key] = { current: undefined, compare: value }
    } else {
      keyToValues[key].compare = value
    }
  })

  return Object.values(keyToValues)
}

export const findDuplicateElements = <T>(array: T[]): T[] => {
  const sortedArray = array.slice().sort()
  const results: T[] = []

  for (let i = 0; i < sortedArray.length - 1; i++) {
    if (sortedArray[i + 1] === sortedArray[i]) {
      results.push(sortedArray[i])
    }
  }

  return results
}
// inspired by: https://stackoverflow.com/a/60132060
export const arrayOfAllUnique =
  <Union extends string | number>() =>
  <Tuple extends Union[]>(
    array: Tuple &
      ([Exclude<Union, Tuple[number]>] extends [never]
        ? unknown
        : `Array is missing a value ${Exclude<Union, Tuple[number]>}`),
  ) => [...new Set(array)]

/**
 * Filters duplicate objects out of the array.
 *
 * A naive `[...new Set(objects)]` approach cannot be used because
 * different object identities always cause objects to appear different,
 * even if all their keys and values match.
 *
 * Note that for objects with the same keys and values to be considered equal,
 * the order in which the keys were defined in the object must also be equal.
 */
export const removeDuplicateObjects = <T extends Record<string, unknown>>(
  objects: T[],
): T[] =>
  Object.values(
    Object.fromEntries(
      objects.map((object) => [JSON.stringify(object), object]),
    ),
  )

/**
 * Groups the elements of an array into a `Map`, using the provided key extractor function.
 *
 * A `Map` preserves the key type, so numeric and symbol keys survive grouping instead of
 * being coerced to strings, and insertion order is kept rather than numeric keys being
 * hoisted the way object keys are.
 *
 * @template T - The type of elements in the input array.
 * @template K - The type of the key.
 * @param items - The array of items to group.
 * @param keyExtractor - A function that extracts the key for each item.
 * @returns A `Map` where each key is produced by the keyExtractor and the value is an array of items with that key.
 *
 * @example
 * const data = [
 *   { type: 'fruit', name: 'apple' },
 *   { type: 'vegetable', name: 'carrot' },
 *   { type: 'fruit', name: 'banana' }
 * ]
 * const grouped = groupBy(data, item => item.type)
 * // grouped = Map {
 * //   'fruit' => [{ type: 'fruit', name: 'apple' }, { type: 'fruit', name: 'banana' }],
 * //   'vegetable' => [{ type: 'vegetable', name: 'carrot' }]
 * // }
 */
export function groupBy<T, K>(
  items: T[],
  keyExtractor: (item: T) => K,
): Map<K, T[]> {
  return items.reduce((acc, item) => {
    const key = keyExtractor(item)
    const group = acc.get(key)
    if (group) {
      group.push(item)
    } else {
      acc.set(key, [item])
    }
    return acc
  }, new Map<K, T[]>())
}

/**
 * Equivalent to `items.map(mapper).find(Boolean)` without having to iterate the entire array.
 *
 * The mapper will be called with both the index and original `items`
 * array, although it is not expected that they will be needed often.
 * */
export const findMap = <T, U>(
  items: T[],
  // oxlint-disable-next-line max-params
  mapper: (value: T, index: number, obj: T[]) => U,
): U | undefined => {
  // This has been benchmarked in Firefox - for 155'000 op/s - compared to:
  // - .find(x => mapper(x)), mapper()      for  27'600 op/s
  // - for (const item of …)                for  22'600 op/s
  // - .map(mapper).find(Boolean)           for  13'000 op/s
  // - .flatMap(…)                          for   9'800 op/s
  // - .entries().map(mapper).find(Boolean) for   1'300 op/s
  //
  // We do run the mapper twice, but there's no need to allocate
  // the entire mapped object, or to iterate to the end.
  const index = items.findIndex(mapper)
  return index === -1 ? undefined : mapper(items[index], index, items)
}

/**
 * `Array.find` and `Array.findIndex` combined, where the
 * callback is only called if the item is not nullable.
 *
 * The first item in the returned list is the found item,
 * and the second item is the index or -1.
 * */
export const findNotEmptyWithIndex = <T>(
  items: Optional<T[]>,
  // oxlint-disable-next-line max-params -- copied signature from Array.findIndex
  predicate: (value: NonNullable<T>, index: number, obj: T[]) => unknown,
): [null, -1] | [NonNullable<T>, number] => {
  if (!items) {
    return [null, -1]
  }
  const index = items.findIndex(
    // oxlint-disable-next-line max-params -- signature from Array.findIndex
    (value, index, obj) => notEmpty(value) && predicate(value, index, obj),
  )
  const item = index === -1 ? null : items[index]
  return item ? [item, index] : [null, -1]
}
